import { Logger } from '@/utils/logger';
import { useLayoutEffect, useRef } from 'react';

const logger = new Logger('useClearStaleShippingOptionSelection');

type SelectableDeliveryOption = { name: string; expectedFulfilmentDate?: string } | null;

/**
 * The shipping option is selected by name, so a refetch of the delivery options can leave the selection pointing at a
 * different promise to the one the customer picked: at the cutoff a selected same day option comes back for tomorrow,
 * or the option isn't offered any more. Clears the selection in either case, so the customer has to pick again.
 *
 * @param props - the selected option's name, the shown options by name (null when an option isn't shown), whether the
 * options are being (re)fetched, and a stable callback that clears the selection
 * @param props.selectedShippingOption
 * @param props.selectableDeliveryOptions
 * @param props.isLoading
 * @param props.clearSelection
 */
export function useClearStaleShippingOptionSelection(props: {
  selectedShippingOption: string | undefined;
  selectableDeliveryOptions: Record<string, SelectableDeliveryOption>;
  isLoading: boolean;
  clearSelection: () => void;
}) {
  const { selectedShippingOption, selectableDeliveryOptions, isLoading, clearSelection } = props;
  const lastSelection = useRef<{ name: string; expectedFulfilmentDate?: string } | null>(null);

  // NOTE: Layout effect so a stale selection is cleared before it's rendered
  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }

    if (!selectedShippingOption) {
      lastSelection.current = null;
      return;
    }

    const option = selectableDeliveryOptions[selectedShippingOption] ?? null;
    const hasExpectedFulfilmentDateChanged =
      lastSelection.current?.name === selectedShippingOption &&
      lastSelection.current.expectedFulfilmentDate !== option?.expectedFulfilmentDate;

    if (!option || hasExpectedFulfilmentDateChanged) {
      logger.info('clearing stale shipping option selection', {
        selectedShippingOption,
        reason: option ? 'expectedFulfilmentDate changed' : 'no longer offered',
        previousExpectedFulfilmentDate: lastSelection.current?.expectedFulfilmentDate,
        expectedFulfilmentDate: option?.expectedFulfilmentDate,
      });
      lastSelection.current = null;
      clearSelection();
      return;
    }

    lastSelection.current = { name: selectedShippingOption, expectedFulfilmentDate: option.expectedFulfilmentDate };
  }, [clearSelection, isLoading, selectableDeliveryOptions, selectedShippingOption]);
}
